/**
 * FAQTinted — V01 "Numbered + Tinted" (handoff round 2 #3)
 *
 * Non-collapsing FAQ cards with 4% blue tint, sans question + serif answer,
 * subtle hover tint shift. Inline styles bypass dev CSS chunk caching.
 */

import * as React from 'react'
import { PartLinkedText } from '@/components/filters/part-linked-text'
import type { PartLinkSegment } from '@/lib/filters/article-part-links'

const BLUE = '#0469f0'
const BLUE_TINT_4 = 'rgba(4, 105, 240, 0.04)'
const BLUE_TINT_6 = 'rgba(4, 105, 240, 0.06)'
const GRAY_700 = '#374151'
const GRAY_900 = '#111827'

const SYS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
const SERIF = 'Charter, "Source Serif Pro", Georgia, "Times New Roman", serif'

export interface FAQTintedItem {
  question: string
  answer: string
  /** FRIDGE-1087 — the answer's filter numbers as linked segments; absent = plain text. */
  answerSegments?: PartLinkSegment[] | null
}

export interface FAQTintedProps {
  eyebrow?: string
  title?: string
  items: FAQTintedItem[]
  className?: string
}

function FAQRow({ item }: { item: FAQTintedItem }) {
  const [hover, setHover] = React.useState(false)
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? BLUE_TINT_6 : BLUE_TINT_4,
        borderRadius: 10,
        padding: '18px 22px',
        transition: 'background 150ms linear',
      }}
    >
      <p
        style={{
          fontFamily: 'inherit',
          fontSize: 20,
          fontWeight: 800,
          letterSpacing: '-0.015em',
          color: GRAY_900,
          margin: '0 0 8px',
          lineHeight: 1.3,
        }}
      >
        {item.question}
      </p>
      <p
        style={{
          fontFamily: 'inherit',
          fontSize: 17,
          lineHeight: 1.65,
          color: GRAY_700,
          margin: 0,
        }}
      >
        <PartLinkedText text={item.answer} segments={item.answerSegments} />
      </p>
    </div>
  )
}

export function FAQTinted({ title = 'FAQ', items }: FAQTintedProps) {
  return (
    <section style={{ margin: '36px 0 24px' }}>
      {title && (
        <h2
          style={{
            fontFamily: 'inherit',
            fontSize: 30,
            fontWeight: 900,
            letterSpacing: '-0.025em',
            color: GRAY_900,
            margin: '32px 0 12px 0',
            textWrap: 'balance' as never,
          }}
        >
          {title}
        </h2>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((it, i) => (
          <FAQRow key={i} item={it} />
        ))}
      </div>
    </section>
  )
}
